import React from 'react';
import { Navigate, useLocation } from 'react-router';
import { useAuth } from '@hooks/useAuth';
import { usePermission } from '@/context/PermissionsContext';
import { useOnboardingGate } from '@/context/OnboardingGateContext';
import { useAssetsAccess } from '@/features/assets/useAssetsAccess';
import { hasValidToken } from '@/services/authService';
import { ProtectedRouteProps } from '@/types';
import LoadingSpinner from '@/components/common/atoms/LoadingSpinner';
import AccessDenied from '@/components/common/organisms/AccessDenied';

interface ProtectedRoutePropsExtended extends ProtectedRouteProps {
  requiredPermission?: { module: string; action: string };
  /** Gate on the API's Assets:AllowedEmails list (see useAssetsAccess). */
  requiresAssetsAccess?: boolean;
}

/**
 * Routes an employee may still reach while their onboarding is unsubmitted. Everything else
 * redirects to the wizard. Password changes stay open so nobody can be locked out of their
 * own account by an unfinished form; logging out is an action, not a route, so it always works.
 */
const ONBOARDING_ALLOWED_PATHS = ['/onboarding', '/change-password', '/set-password'];

const ProtectedRoute: React.FC<ProtectedRoutePropsExtended> = ({ children, requiredPermission, requiresAssetsAccess }) => {
  const { user, loading: authLoading } = useAuth();
  const { can, isLoaded: permissionsLoaded } = usePermission();
  const { needsOnboarding, isResolved: onboardingResolved } = useOnboardingGate();
  const assetsAllowed = useAssetsAccess();
  const location = useLocation();

  // Wait for auth to load
  if (authLoading) {
    return <LoadingSpinner />;
  }

  // Redirect to login if not authenticated. The cached `user` object alone is not
  // proof of a session — it is plain stored JSON and can outlive the token — so the
  // gate is the token: present, well-formed and unexpired.
  if (!user || !hasValidToken()) {
    return <Navigate to="/login" replace />;
  }

  // Onboarding lock: an employee who has not submitted their wizard cannot go anywhere else.
  // This runs before the permission check so an incomplete employee never sees Access Denied
  // when the real answer is "finish onboarding first".
  if (!ONBOARDING_ALLOWED_PATHS.includes(location.pathname)) {
    // Wait for the status rather than flashing the destination and yanking it away.
    if (!onboardingResolved) {
      return <LoadingSpinner />;
    }

    if (needsOnboarding) {
      return <Navigate to="/onboarding" replace />;
    }
  }

  // If permissions are required, check them
  if (requiredPermission) {
    // Wait for permissions to load
    if (!permissionsLoaded) {
      return <LoadingSpinner />;
    }

    // Check if user has the required permission
    if (!can(requiredPermission.module, requiredPermission.action)) {
      return <AccessDenied />;
    }
  }

  // Assets module: the API's Assets:AllowedEmails list decides, and it is the same list the
  // asset endpoints enforce — so a hidden menu is not the only thing standing in the way.
  if (requiresAssetsAccess) {
    if (assetsAllowed === null) {
      return <LoadingSpinner />;
    }

    if (!assetsAllowed) {
      return <AccessDenied />;
    }
  }

  return <>{children}</>;
};

export default ProtectedRoute;
